/**
 * Destinos que não são cidades (módulo puro — sem IA, sem Maps, sem créditos).
 *
 * Problema real: "Bali" não é uma cidade, é uma ilha/província. Quando o
 * geocoder devolve uma área (ilha, província, estado, região, arquipélago), a
 * validação estrita por localidade rejeita TODAS as paragens (elas ficam em
 * Denpasar, Ubud, Kuta...) e a rota sai vazia — "não tem rota".
 *
 * A regra é genérica: se o geocoder não devolveu uma localidade (city/town/
 * village) ou classificou o resultado como área administrativa/natural mais
 * larga, o pedido passa a ter âmbito regional (raio grande, sem exigir que o
 * nome da localidade coincida).
 */

const AREA_TYPES = new Set([
  "island",
  "islet",
  "archipelago",
  "state",
  "province",
  "region",
  "county",
  "district",
  "territory",
  "peninsula",
  "national_park",
  "protected_area",
  "administrative",
]);

export type AreaDestinationProbe = {
  /** Nominatim `addresstype` */
  addresstype?: string | null;
  /** Nominatim `type` */
  type?: string | null;
  /** Nominatim `class` */
  category?: string | null;
  address?: Record<string, string | undefined> | null;
};

/** True quando o destino pedido é uma área e não uma cidade concreta. */
export function isAreaDestination(probe: AreaDestinationProbe | null | undefined): boolean {
  if (!probe) return false;
  const address = probe.address ?? {};
  const hasLocality = Boolean(
    address.city || address.town || address.village || address.municipality,
  );
  const kind = (probe.addresstype || probe.type || "").toLowerCase().trim();
  const cls = (probe.category || "").toLowerCase().trim();

  if (kind === "city" || kind === "town" || kind === "village" || kind === "municipality") {
    return false;
  }
  if (cls === "natural" || cls === "place") {
    if (AREA_TYPES.has(kind)) return true;
  }
  if (AREA_TYPES.has(kind)) return true;
  // Sem localidade nenhuma no endereço, o ponto devolvido representa uma área.
  return !hasLocality;
}
